import React, { useCallback, useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import ContentDashboard from 'react-ui-components/components/Content/ContentDashboard';
import { firebaseUserToUser } from 'react-ui-components/components/Content/AccessControlProvider';
import {
  ContentAssignmentFilter,
  ContentAssignmentScopeOption,
  ContentAssignmentScopeType,
  ContentAssignmentTargetKey,
  ContentCategory,
  ContentItem,
} from 'react-ui-components/components/Content/IContentData';
import {
  deleteContentAssignmentsForContent,
  hydrateContentItemsWithAssignments,
  stripHydratedContentAssignments,
  syncContentAssignments,
} from 'react-ui-components/components/Content/contentAssignments';
import { PopupService } from 'react-ui-components/components/PopupService';
import { LoadingService } from 'react-ui-components/components/LoadingService';
import { removeUndefinedFields } from '../../utils';

interface ContentManagerProps {
  defaultAssignmentFilter?: ContentAssignmentFilter;
  lockedAssignmentFilter?: boolean;
  allowedAssignmentTargetKeys?: ContentAssignmentTargetKey[];
  defaultAssignmentScope?: {
    scopeType: ContentAssignmentScopeType;
    scopeId: string;
  };
  lockAssignmentScope?: boolean;
}

const createScopeOption = (
  id: string,
  label: string,
  description?: string,
): ContentAssignmentScopeOption => ({
  id,
  label: label || id,
  description,
});

const ContentManager: React.FC<ContentManagerProps> = ({
  defaultAssignmentFilter,
  lockedAssignmentFilter = false,
  allowedAssignmentTargetKeys,
  defaultAssignmentScope,
  lockAssignmentScope = false,
}) => {
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [content, setContent] = useState<ContentItem[]>([]);
  const [user, setUser] = useState<any>(null);
  const [assignmentScopeOptions, setAssignmentScopeOptions] = useState<
    Partial<Record<ContentAssignmentScopeType, ContentAssignmentScopeOption[]>>
  >({});

  const fetchCategories = useCallback(async () => {
    const categoriesQuery = query(collection(db, 'content-categories'), orderBy('order'));
    const categoriesSnapshot = await getDocs(categoriesQuery);
    const categoriesData: ContentCategory[] = categoriesSnapshot.docs.map((categoryDoc) => ({
      id: categoryDoc.id,
      ...(categoryDoc.data() as ContentCategory),
    }));

    setCategories(categoriesData);
  }, []);

  const fetchAssignmentScopeOptions = useCallback(async () => {
    const [rafflesSnapshot, sponsorsSnapshot, featuredBusinessesSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'raffles'), orderBy('raffleDate', 'desc'), limit(200))),
      getDocs(query(collection(db, 'sponsors'), orderBy('name'), limit(200))),
      getDocs(query(collection(db, 'featured-businesses'), orderBy('order'), limit(200))),
    ]);

    setAssignmentScopeOptions({
      global: [createScopeOption('global', 'Global')],
      raffle: rafflesSnapshot.docs.map((raffleDoc) =>
        createScopeOption(
          raffleDoc.id,
          String(
            raffleDoc.data()?.title ||
              raffleDoc.data()?.name ||
              raffleDoc.data()?.id ||
              raffleDoc.id,
          ),
        ),
      ),
      sponsor: sponsorsSnapshot.docs.map((sponsorDoc) =>
        createScopeOption(
          sponsorDoc.id,
          String(sponsorDoc.data()?.name || sponsorDoc.data()?.id || sponsorDoc.id),
        ),
      ),
      featuredBusiness: featuredBusinessesSnapshot.docs.map((featuredBusinessDoc) =>
        createScopeOption(
          featuredBusinessDoc.id,
          String(
            featuredBusinessDoc.data()?.name ||
              featuredBusinessDoc.data()?.title ||
              featuredBusinessDoc.data()?.id ||
              featuredBusinessDoc.id,
          ),
        ),
      ),
    });
  }, []);

  const fetchContent = useCallback(async () => {
    const [contentSnapshot, assignmentsSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'content'), orderBy('order'), limit(250))),
      getDocs(collection(db, 'content-assignments')),
    ]);

    const contentItems: ContentItem[] = contentSnapshot.docs.map((contentDoc) => ({
      id: contentDoc.id,
      ...(contentDoc.data() as ContentItem),
    }));
    const assignments = assignmentsSnapshot.docs.map((assignmentDoc) => ({
      id: assignmentDoc.id,
      ...(assignmentDoc.data() as any),
    }));

    setContent(hydrateContentItemsWithAssignments(contentItems, assignments));
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      LoadingService.setLoading(true);
      await Promise.all([
        fetchCategories(),
        fetchAssignmentScopeOptions(),
        fetchContent(),
      ]);
    } catch (error: any) {
      PopupService.pushPopup(`Error loading content manager data: ${error.message}`);
    } finally {
      LoadingService.setLoading(false);
    }
  }, [fetchAssignmentScopeOptions, fetchCategories, fetchContent]);

  useEffect(() => {
    const initializeUser = async () => {
      const currentUser = await firebaseUserToUser(auth.currentUser);
      setUser(currentUser);
    };

    initializeUser();
    refreshAll();
  }, [refreshAll]);

  const handleSaveContent = useCallback(
    async (
      contentItem: ContentItem,
      isEdit?: boolean,
      currentContent?: ContentItem | null,
    ) => {
      try {
        LoadingService.setLoading(true);

        const contentId = contentItem.id || currentContent?.id || `content_${Date.now()}`;
        const now = new Date().toISOString();
        const contentRef = doc(db, 'content', contentId);

        const baseContentData = stripHydratedContentAssignments({
          ...contentItem,
          id: contentId,
        });
        const cleanData = removeUndefinedFields(baseContentData) || {};
        const contentData = {
          ...cleanData,
          id: contentId,
          updatedAt: now,
          createdAt:
            isEdit && currentContent?.createdAt
              ? currentContent.createdAt
              : contentItem.createdAt || now,
        };

        await setDoc(contentRef, contentData, { merge: true });

        await syncContentAssignments({
          db,
          content: {
            ...contentItem,
            id: contentId,
          },
          contentId,
          allowedTargetKeys: allowedAssignmentTargetKeys,
          lockedScope:
            lockAssignmentScope && defaultAssignmentScope
              ? defaultAssignmentScope
              : undefined,
        });

        PopupService.pushPopup(
          isEdit ? 'Content updated successfully' : 'Content created successfully',
        );
        await fetchContent();
      } catch (error: any) {
        PopupService.pushPopup(`Error saving content: ${error.message}`);
        throw error;
      } finally {
        LoadingService.setLoading(false);
      }
    },
    [
      allowedAssignmentTargetKeys,
      defaultAssignmentScope,
      fetchContent,
      lockAssignmentScope,
    ],
  );

  const handleDeleteContent = useCallback(async (contentItem: ContentItem) => {
    try {
      LoadingService.setLoading(true);

      if (!contentItem.id) {
        return;
      }

      await deleteDoc(doc(db, 'content', contentItem.id));
      await deleteContentAssignmentsForContent(db, contentItem.id);

      PopupService.pushPopup('Content deleted successfully');
      setContent((previousContent) =>
        previousContent.filter((item) => item.id !== contentItem.id),
      );
    } catch (error: any) {
      PopupService.pushPopup(`Error deleting content: ${error.message}`);
      throw error;
    } finally {
      LoadingService.setLoading(false);
    }
  }, []);

  const handleDuplicateContent = useCallback(
    async (contentItem: ContentItem) => {
      try {
        LoadingService.setLoading(true);

        const contentId = `content_${Date.now()}`;
        const now = new Date().toISOString();
        const duplicatedContent: ContentItem = {
          ...(stripHydratedContentAssignments(contentItem) as ContentItem),
          id: contentId,
          title: `${contentItem.title} (Copy)`,
          createdAt: now,
          updatedAt: now,
          assignments: [],
        };

        await setDoc(doc(db, 'content', contentId), duplicatedContent);

        PopupService.pushPopup('Content duplicated successfully');
        await fetchContent();
      } catch (error: any) {
        PopupService.pushPopup(`Error duplicating content: ${error.message}`);
        throw error;
      } finally {
        LoadingService.setLoading(false);
      }
    },
    [fetchContent],
  );

  const handleSaveCategory = useCallback(
    async (category: ContentCategory) => {
      try {
        LoadingService.setLoading(true);

        const categoryId = category.id || `cat_${Date.now()}`;
        const categoryRef = doc(db, 'content-categories', categoryId);
        const now = new Date().toISOString();
        const cleanData = removeUndefinedFields({
          ...category,
          id: categoryId,
          updatedAt: now,
          createdAt: category.createdAt || now,
        });

        await setDoc(categoryRef, cleanData as Record<string, any>, { merge: true });
        PopupService.pushPopup(
          category.id ? 'Category updated successfully' : 'Category created successfully',
        );
        await fetchCategories();
      } catch (error: any) {
        PopupService.pushPopup(`Error saving category: ${error.message}`);
        throw error;
      } finally {
        LoadingService.setLoading(false);
      }
    },
    [fetchCategories],
  );

  const handleDeleteCategory = useCallback(async (category: ContentCategory) => {
    try {
      LoadingService.setLoading(true);

      if (!category.id) {
        return;
      }

      await deleteDoc(doc(db, 'content-categories', category.id));
      PopupService.pushPopup('Category deleted successfully');
      setCategories((previousCategories) =>
        previousCategories.filter((item) => item.id !== category.id),
      );
    } catch (error: any) {
      PopupService.pushPopup(`Error deleting category: ${error.message}`);
      throw error;
    } finally {
      LoadingService.setLoading(false);
    }
  }, []);

  return (
    <ContentDashboard
      onSaveContent={handleSaveContent}
      onDeleteContent={handleDeleteContent}
      onDuplicateContent={handleDuplicateContent}
      onSaveCategory={handleSaveCategory}
      onDeleteCategory={handleDeleteCategory}
      initialCategories={categories}
      initialContent={content}
      currentUser={user}
      assignmentScopeOptions={assignmentScopeOptions}
      allowedAssignmentTargetKeys={allowedAssignmentTargetKeys}
      defaultAssignmentFilter={defaultAssignmentFilter}
      lockedAssignmentFilter={lockedAssignmentFilter}
      defaultAssignmentScope={defaultAssignmentScope}
      lockAssignmentScope={lockAssignmentScope}
    />
  );
};

export default ContentManager;
